import { FunctionComponent, useEffect, Suspense, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { getCurrency } from 'dibs-intl/exports/units';
import { Link } from 'dibs-elements/exports/Link';
import ArrowUp from 'dibs-icons/exports/legacy/ArrowUp';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { isMwCheckoutOptimizationsTestActive } from '../../helpers/mwCheckoutOptimizationsTestHelpers';
import { useAppDispatch, useCheckoutSelector } from '../../reducers/root';
import { toggleSummaryAction } from '../../actions/commonActions';
import { CheckoutMobileSummaryPaneTitle } from './CheckoutMobileSummaryPaneTitle';
import { CheckoutMobileSummaryPriceTable } from './CheckoutMobileSummaryPriceTable';
import { CheckoutMobileSection } from '../shared/CheckoutMobileSection';
import { CheckoutDutiesMessage } from '../../shared/helpers/CheckoutDutiesMessage';
import CheckoutAvalaraTaxExempt from '../containers/CheckoutAvalaraTaxExempt';
import { CheckoutNegotiationDisclaimer } from '../shared/CheckoutNegotiationDisclaimer';
import { useCustomsMessagingQuery } from '../../hooks/useCustomsMessagingQuery';
import { useMobileSummaryTracking } from './useMobileSummaryTracking';
import { trackSummaryToggle } from '../../helpers/checkoutTracking';
import CheckoutSidebarPromotion from '../../sidebar/promotion/CheckoutSidebarPromotion';

import { CheckoutMobileSummaryPane_viewer$key } from './__generated__/CheckoutMobileSummaryPane_viewer.graphql';
import { CheckoutMobileSummaryPane_transaction$key } from './__generated__/CheckoutMobileSummaryPane_transaction.graphql';
import { CheckoutMobileSummaryPane_item$key } from './__generated__/CheckoutMobileSummaryPane_item.graphql';
import { CheckoutMobileSummaryPane_buyer$key } from './__generated__/CheckoutMobileSummaryPane_buyer.graphql';

import styles from './styles/CheckoutMobileSummaryPane.scss';

type Props = {
    isComputing: boolean;
    viewer: CheckoutMobileSummaryPane_viewer$key | null;
    transaction: CheckoutMobileSummaryPane_transaction$key | null;
    item: CheckoutMobileSummaryPane_item$key | null;
    buyer: CheckoutMobileSummaryPane_buyer$key | null;
};

export const CheckoutMobileSummaryPane: FunctionComponent<Props> = props => {
    const { isComputing } = props;
    const trackingFired = useRef(false);

    const dispatch = useAppDispatch();
    const isSummaryOpen = useCheckoutSelector(state => state.pane.isSummaryOpen);
    const currentStep = useCheckoutSelector(state => state.pane.currentPane);

    useEffect(() => {
        if (isSummaryOpen && !trackingFired.current) {
            trackAbTestV2Variant('removePromoCodeAutofillCheckout');
            trackingFired.current = true;
        }
    }, [isSummaryOpen]);

    const viewer = useFragment(
        graphql`
            fragment CheckoutMobileSummaryPane_viewer on Viewer {
                ...CheckoutSidebarPromotion_viewer
                ...CheckoutDutiesMessage_viewer
                ...CheckoutAvalaraTaxExempt_viewer
            }
        `,
        props.viewer
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutMobileSummaryPane_transaction on Transaction {
                ...CheckoutMobileSummaryPaneTitle_transaction
                ...CheckoutSummaryItemSkuDetails_transaction
                ...CheckoutMobileSummaryPriceTable_transaction
                ...CheckoutSidebarPromotion_transaction
                ...CheckoutDutiesMessage_transaction
                ...CheckoutTotalsDutiesMessage_transaction
                ...CheckoutAvalaraTaxExempt_transaction
                buyerChargeCurrency
                isNegotiable
            }
        `,
        props.transaction
    );

    const item = useFragment(
        graphql`
            fragment CheckoutMobileSummaryPane_item on Item {
                ...CheckoutMobileSummaryPaneTitle_item
                ...CheckoutSummaryItemSkuDetails_item
                ...CheckoutMobileSummaryPriceTable_item
                ...CheckoutSidebarPromotion_item
                ...useCustomsMessagingQuery_item
            }
        `,
        props.item
    );

    const buyer = useFragment(
        graphql`
            fragment CheckoutMobileSummaryPane_buyer on User {
                ...useCustomsMessagingQuery_buyer
            }
        `,
        props.buyer
    );

    useMobileSummaryTracking({ isReady: !!transaction && !!item });

    const arrowStyles = classnames(styles.arrow, { [styles.arrowDown]: !isSummaryOpen });

    const intl = useIntl();
    const defaultCurrency = getCurrency(intl.locale);
    const currency = transaction?.buyerChargeCurrency || defaultCurrency;
    const dutiesMessagingQueryReference = useCustomsMessagingQuery({ buyer, item });

    function handleClick(): void {
        dispatch(toggleSummaryAction({ isSummaryOpen: !isSummaryOpen }));
        trackSummaryToggle(!isSummaryOpen, currentStep);
    }

    const isMwCheckoutOptimizationEnabled = isMwCheckoutOptimizationsTestActive();

    return (
        <>
            <CheckoutMobileSection className={styles.pane}>
                <Link className={styles.trigger} onClick={handleClick}>
                    <CheckoutMobileSummaryPaneTitle
                        item={item}
                        transaction={transaction}
                        isExpanded={isSummaryOpen}
                    />
                    <ArrowUp className={arrowStyles} />
                </Link>
                {isSummaryOpen && transaction && item && (
                    <div className={styles.content}>
                        <CheckoutMobileSummaryPriceTable
                            transaction={transaction}
                            item={item}
                            isComputing={isComputing}
                        />
                        {!isMwCheckoutOptimizationEnabled && (
                            <Suspense>
                                <CheckoutDutiesMessage
                                    viewer={viewer}
                                    isComputing={isComputing}
                                    transaction={transaction}
                                    currency={currency}
                                    isMobile
                                    queryReference={dutiesMessagingQueryReference || null}
                                />
                            </Suspense>
                        )}
                        <div className={dibsCss.mtSmall}>
                            {!isMwCheckoutOptimizationEnabled && (
                                <div className={dibsCss.mbXsmall}>
                                    <FormattedMessage
                                        id="dibs-checkout.CheckoutMobileSummaryPane.add_promo_title"
                                        defaultMessage="Add Promo Code"
                                    />
                                </div>
                            )}
                            <CheckoutSidebarPromotion
                                viewer={viewer}
                                item={item}
                                transaction={transaction}
                                isComputing={isComputing}
                                trackingSource="Order Summary"
                                openByDefault={!isMwCheckoutOptimizationEnabled}
                            />
                        </div>
                        <CheckoutAvalaraTaxExempt
                            isComputing={isComputing}
                            transaction={transaction}
                            viewer={viewer}
                        />
                    </div>
                )}
            </CheckoutMobileSection>
            {transaction?.isNegotiable && isSummaryOpen && (
                <CheckoutMobileSection className={styles.negotiationDisclaimer}>
                    <CheckoutNegotiationDisclaimer />
                </CheckoutMobileSection>
            )}
        </>
    );
};
